import React, { useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import e2iApi from '../../services/e2iApi';
import type { Cir, CirSaveInput, Job } from '../../types/e2i';
import { JOB_STATUS } from '../../types/e2i';
import { errorText, formatAmount, jobInvoiceable, openJobLines, plural } from '../../utils/e2i';
import CirCard from './CirCard';
import CirDialog from './CirDialog';
import { StatusChip } from './StatusChip';
import { WorkspaceTabProps, quotationOfJob, vatRateOf } from './workspace';

/** The jobs' client invoice requests: raise them on whole job lines, submit, and (finance) book or return them. */
const CirTab: React.FC<WorkspaceTabProps> = ({ ws, config, currency, onWorkspace, reload, notify, goTo }) => {
  const perms = ws.permissions;
  const [dialog, setDialog] = useState<{ open: boolean; job: Job | null; cir: Cir | null }>({ open: false, job: null, cir: null });

  const jobs = (ws.jobs || []).filter((j) => j.status !== 'Cancelled' && (jobInvoiceable(j) || (ws.cirs || []).some((c) => c.jobId === j.jobId)));
  const vatOf = (job: Job): number => {
    const q = quotationOfJob(ws, job);
    if (q) return Number(q.vatRate) || 0;
    return vatRateOf(config, config.defaultVatCode, 0);
  };
  const poOf = (job: Job): string | null => {
    const q = quotationOfJob(ws, job);
    return q?.client?.poRef || q?.clientRef || null;
  };

  const save = async (input: CirSaveInput, files: File[]) => {
    const job = dialog.job;
    if (!job) return;
    if (dialog.cir) {
      await e2iApi.saveCir(dialog.cir.cirId, input);
      setDialog((d) => ({ ...d, open: false }));
      await reload();
      notify(`${dialog.cir.cirNo || 'Invoice request'} saved`, 'success');
      return;
    }
    const before = new Set((ws.cirs || []).map((c) => c.cirId));
    const next = await e2iApi.createCir(job.jobId, input);
    onWorkspace(next);
    setDialog((d) => ({ ...d, open: false }));
    const created = (next.cirs || []).filter((c) => c.jobId === job.jobId && !before.has(c.cirId)).sort((a, b) => b.cirId - a.cirId)[0];
    if (created && files.length) {
      try {
        for (const f of files) await e2iApi.uploadAttachment('cir', created.cirId, f);
      } catch (e: any) {
        notify(`${created.cirNo || 'The invoice request'} was created, but a file was not attached: ${errorText(e, 'the upload failed')}`, 'warning');
        await reload();
        return;
      }
      await reload();
    }
    notify(`${created?.cirNo || 'The invoice request'} was created; submit it to finance when it is ready`, 'success');
  };

  if (!jobs.length) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Invoice requests are raised on a job once its sales order is in SAP (finance added the order draft).
          </Typography>
          <Button variant="outlined" onClick={() => goTo('job')}>Go to the job</Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {jobs.map((job) => {
        const open = openJobLines(job);
        const cirs = (ws.cirs || []).filter((c) => c.jobId === job.jobId).sort((a, b) => b.cirId - a.cirId);
        return (
          <Box component="section" key={job.jobId} aria-label={`Invoice requests of ${job.jobNo || 'the job'}`}>
            <Paper sx={{ p: 2, mb: 1.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>{job.jobNo || 'Job'}</Typography>
                <StatusChip status={JOB_STATUS[job.status]} fallback={job.status} />
                <Typography variant="body2" color="text.secondary">
                  Invoiced {formatAmount(job.invoicedTotal)} of {formatAmount(job.total)} {currency} · {plural(open.length, 'line')} not on a request yet
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {perms.canCreateCir && jobInvoiceable(job) && open.length ? (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, job, cir: null })}>New invoice request</Button>
                ) : null}
              </Stack>
            </Paper>
            {cirs.length ? (
              <Stack spacing={1.5}>
                {cirs.map((c) => (
                  <CirCard key={c.cirId} cir={c} currency={currency} notify={notify} onChanged={reload} onEdit={() => setDialog({ open: true, job, cir: c })} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>No invoice requests yet.</Typography>
            )}
          </Box>
        );
      })}

      {dialog.job ? (
        <CirDialog
          open={dialog.open}
          job={dialog.job}
          cir={dialog.cir}
          defaultTrn={ws.enquiry.customer?.federalTaxId || null}
          defaultPoRef={poOf(dialog.job)}
          vatRate={vatOf(dialog.job)}
          config={config}
          currency={currency}
          onClose={() => setDialog((d) => ({ ...d, open: false }))}
          onSave={save}
        />
      ) : null}
    </Stack>
  );
};

export default CirTab;
